import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Avatar,
  Tabs,
  Tab,
  Stack
} from "@mui/material";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import SettingsIcon from "@mui/icons-material/Settings";

import TabPanel from "../../components/TabPanel";
import SchedulesForm from "../../components/SchedulesForm";
import CompaniesManager from "../../components/CompaniesManager";
import PlansManager from "../../components/PlansManager";
import HelpsManager from "../../components/HelpsManager";
import Options from "../../components/Settings/Options";
import Whitelabel from "../../components/Settings/Whitelabel";

import { i18n } from "../../translate/i18n.js";
import { toast } from "react-toastify";

import useCompanies from "../../hooks/useCompanies";
import { AuthContext } from "../../context/Auth/AuthContext";

import OnlyForSuperUser from "../../components/OnlyForSuperUser";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import useSettings from "../../hooks/useSettings";
import ForbiddenPage from "../../components/ForbiddenPage/index.js";

const SettingsCustom = () => {
  const themeV4 = useThemeV4();
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";

  const [tab, setTab] = useState("options");
  const [schedules, setSchedules] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [settings, setSettings] = useState({});
  const [oldSettings, setOldSettings] = useState({});
  const [schedulesEnabled, setSchedulesEnabled] = useState(false);

  const { find, updateSchedules } = useCompanies();
  const { getAll: getAllSettings } = useCompanySettings();
  const { getAll: getAllSettingsOld } = useSettings();
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    async function findData() {
      setLoading(true);
      try {
        const companyId = user.companyId;
        const company = await find(companyId);
        const settingList = await getAllSettings(companyId);
        const settingListOld = await getAllSettingsOld();

        setCompany(company);
        setSchedules(company.schedules);
        setSettings(settingList);
        setOldSettings(settingListOld);
        setSchedulesEnabled(settingList.scheduleType === "company");
        setCurrentUser(user);
      } catch (e) {
        toast.error(e);
      }
      setLoading(false);
    }
    findData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSubmitSchedules = async (data) => {
    setLoading(true);
    try {
      setSchedules(data);
      await updateSchedules({ id: company.id, schedules: data });
      toast.success("Horários atualizados com sucesso.");
    } catch (e) {
      toast.error(e);
    }
    setLoading(false);
  };

  const isSuper = () => {
    return currentUser.super;
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
        bgcolor: "#f8fafc"
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 4 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              bgcolor: PRIMARY_MAIN,
              "& svg": { width: 28, height: 28, color: "#fff" }
            }}
          >
            <SettingsIcon />
          </Avatar>
          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                mb: 0.5,
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.5rem", md: "1.875rem" }
              }}
            >
              {i18n.t("settings.title")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, fontSize: "0.875rem" }}>
              Configure as opções do sistema
            </Typography>
          </Box>
        </Stack>

        {/* Tabs Container */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            bgcolor: "#fff",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden"
          }}
        >
          {/* Tabs Header */}
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  minHeight: 56,
                  color: "text.secondary",
                  "&.Mui-selected": {
                    color: PRIMARY_MAIN
                  }
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: PRIMARY_MAIN,
                  height: 3
                }
              }}
            >
              <Tab label={i18n.t("settings.tabs.options")} value="options" />
              {schedulesEnabled && <Tab label="Horários" value="schedules" />}
              {isSuper() && <Tab label="Empresas" value="companies" />}
              {isSuper() && <Tab label={i18n.t("settings.tabs.plans")} value="plans" />}
              {isSuper() && <Tab label={i18n.t("settings.tabs.helps")} value="helps" />}
              {isSuper() && <Tab label="Whitelabel" value="whitelabel" />}
            </Tabs>
          </Box>

          {/* Tabs Content */}
          <Box
            sx={{
              p: 3,
              maxHeight: "calc(100vh - 280px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px"
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1"
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#888",
                borderRadius: "4px"
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#555"
              }
            }}
          >
            <TabPanel value={tab} name="schedules">
              <SchedulesForm
                loading={loading}
                onSubmit={handleSubmitSchedules}
                initialValues={schedules}
              />
            </TabPanel>

            <OnlyForSuperUser
              user={currentUser}
              yes={() => (
                <>
                  <TabPanel value={tab} name="companies">
                    <CompaniesManager />
                  </TabPanel>

                  <TabPanel value={tab} name="plans">
                    <PlansManager />
                  </TabPanel>

                  <TabPanel value={tab} name="helps">
                    <HelpsManager />
                  </TabPanel>

                  <TabPanel value={tab} name="whitelabel">
                    <Whitelabel settings={oldSettings} />
                  </TabPanel>
                </>
              )}
            />

            <TabPanel value={tab} name="options">
              <Options
                settings={settings}
                oldSettings={oldSettings}
                user={currentUser}
                scheduleTypeChanged={(value) =>
                  setSchedulesEnabled(value === "company")
                }
              />
            </TabPanel>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SettingsCustom;